import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { Loader2, ArrowLeft, CheckCircle } from "lucide-react";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get("mode") === "register" ? "register" : "login";
  const fromClover = searchParams.get("from") === "clover";
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const cloverMerchantName = sessionStorage.getItem("clover_merchant_name") || sessionStorage.getItem("clover_merchant_id") || "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "register") {
        const pendingToken = sessionStorage.getItem("pending_clover_token") || null;
        await register(email, password, name, pendingToken);
        // Clean up Clover session data
        sessionStorage.removeItem("pending_clover_token");
        sessionStorage.removeItem("clover_merchant_id");
        sessionStorage.removeItem("clover_merchant_name");
      } else {
        await login(email, password);
      }
      navigate("/tool");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="auth-page">
      {/* Left - Image Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/2199190/pexels-photo-2199190.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Modern coffee shop"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-50/80" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <Link to="/" className="mb-auto pt-8">
            <span className="font-heading text-3xl font-light text-[#0A1F3B]">oly</span>
            <span className="font-heading text-3xl font-black text-[#0A1F3B] ml-1">Payroll</span>
          </Link>
          <div className="mb-12">
            <p className="text-2xl font-heading font-bold text-[#0A1F3B] leading-tight mb-3">
              Stop spending your time on payroll prep.
            </p>
            <p className="text-slate-600 text-base">
              Upload your Clover shifts, get clean exports in seconds.
            </p>
          </div>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#0A1F3B] mb-8 transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft size={16} /> Back to home
          </Link>

          <div className="lg:hidden mb-8">
            <span className="font-heading text-2xl font-light text-[#0A1F3B]">oly</span>
            <span className="font-heading text-2xl font-black text-[#0A1F3B] ml-1">Payroll</span>
          </div>

          <h1 className="font-heading text-2xl font-bold text-[#0A1F3B] mb-1" data-testid="auth-title">
            {mode === "register" ? "Create your account" : "Welcome back"}
          </h1>
          <p className="text-slate-600 text-sm mb-6">
            {mode === "register"
              ? "Start processing payrolls faster."
              : "Sign in to access your payroll tools."}
          </p>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-lg px-4 py-3 mb-4" data-testid="auth-error">
              {error}
            </div>
          )}

          {fromClover && mode === "register" && (
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-4 flex items-center gap-2" data-testid="clover-banner">
              <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
              <p className="text-sm text-green-800">
                <span className="font-semibold">Connected to Clover{cloverMerchantName ? ` — ${cloverMerchantName}` : ""}</span>
                <br />
                <span className="text-xs text-green-600">Finish creating your account to start syncing shifts.</span>
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-slate-600 mb-1.5">
                  Name
                </label>
                <input
                  data-testid="auth-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="w-full h-11 bg-white border border-slate-200 rounded-lg px-4 text-[#0A1F3B] placeholder-[#475569] focus:outline-none focus:border-[#0A1F3B] focus:ring-1 focus:ring-[#0A1F3B] transition-colors"
                />
              </div>
            )}
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-slate-600 mb-1.5">
                Email
              </label>
              <input
                data-testid="auth-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@business.com"
                required
                className="w-full h-11 bg-white border border-slate-200 rounded-lg px-4 text-[#0A1F3B] placeholder-[#475569] focus:outline-none focus:border-[#0A1F3B] focus:ring-1 focus:ring-[#0A1F3B] transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-slate-600 mb-1.5">
                Password
              </label>
              <input
                data-testid="auth-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 6 characters"
                required
                minLength={6}
                className="w-full h-11 bg-white border border-slate-200 rounded-lg px-4 text-[#0A1F3B] placeholder-[#475569] focus:outline-none focus:border-[#0A1F3B] focus:ring-1 focus:ring-[#0A1F3B] transition-colors"
              />
            </div>

            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#0A1F3B] text-white font-bold rounded-lg hover:-translate-y-0.5 hover:shadow-[0_8px_20px_-4px_rgba(10,31,59,0.3)] transition-all duration-300 disabled:opacity-50 disabled:hover:translate-y-0 flex items-center justify-center gap-2"
            >
              {loading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : mode === "register" ? (
                "Create Account"
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          <p className="text-center text-sm text-slate-600 mt-6">
            {mode === "register" ? (
              <>
                Already have an account?{" "}
                <button
                  onClick={() => { setMode("login"); setError(""); }}
                  className="text-[#0A1F3B] font-semibold hover:underline"
                  data-testid="switch-to-login"
                >
                  Sign in
                </button>
              </>
            ) : (
              <>
                Don&apos;t have an account?{" "}
                <button
                  onClick={() => { setMode("register"); setError(""); }}
                  className="text-[#0A1F3B] font-semibold hover:underline"
                  data-testid="switch-to-register"
                >
                  Create one free
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
